<template>
  <div class="image-editor-tool">
    <div class="compressor-wrapper" :dir="currentLanguage === 'ar' ? 'rtl' : 'ltr'">
      <div class="main-layout" :class="{ 'has-image': isImageLoaded }">
        
        <div class="left-panel">
          <div
            class="drop-zone"
            :class="{ dragging: isDragging }"
            v-show="!isImageLoaded"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            @click="$refs.fileInput.click()"
          >
            <div class="drop-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
            </div>
            <p class="drop-text">{{ currentLanguage === 'ar' ? 'اسحب وأفلت الصورة هنا' : 'Drag & Drop an image here' }}</p>
            <p class="drop-hint">{{ currentLanguage === 'ar' ? 'أو' : 'or' }}</p>
            <button class="choose-btn" @click.stop="$refs.fileInput.click()">{{ currentLanguage === 'ar' ? 'اختر صورة' : 'Choose Image' }}</button>
            <p class="drop-formats">{{ currentLanguage === 'ar' ? 'يدعم JPG و PNG و WebP' : 'Supports JPG, PNG, WebP' }}</p>
            
            <div style="margin-top: 20px;">
              <button class="action-btn" @click.stop="createBlankCanvas">{{ currentLanguage === 'ar' ? 'البدء بلوحة فارغة' : 'Start Blank Canvas' }}</button>
            </div>
          </div>

          <div class="preview-card" v-show="isImageLoaded">
            <div class="preview-label" style="display: flex; justify-content: space-between; align-items: center;">
              <div style="display: flex; gap: 8px;">
                <button class="icon-btn" @click="undo" :disabled="historyIndex <= 0" title="Undo">↶</button>
                <button class="icon-btn" @click="redo" :disabled="historyIndex >= history.length - 1" title="Redo">↷</button>
              </div>
              <div style="display: flex; gap: 8px;">
                 <button class="action-btn-sm" @click="$refs.fileInput.click()">{{ currentLanguage === 'ar' ? 'تغيير الصورة' : 'Replace Image' }}</button>
                 <button class="reset-icon-btn" @click="reset">✕ {{ currentLanguage === 'ar' ? 'مسح' : 'Clear' }}</button>
              </div>
            </div>
            <div class="canvas-container" id="canvasShell" :class="{ 'draw-mode': drawMode }" tabindex="0" @keydown.delete.prevent="deleteSelected" @keydown.backspace.prevent="deleteSelected">
               <canvas 
                 ref="editorCanvas" 
                 class="editor-canvas"
                 @mousedown="startAction"
                 @mousemove="moveAction"
                 @mouseup="endAction"
                 @mouseleave="endAction"
                 @touchstart.prevent="startAction"
                 @touchmove.prevent="moveAction"
                 @touchend.prevent="endAction"
               ></canvas>
            </div>
            <div class="canvas-footer">
              <span v-if="canvasWidth">{{ canvasWidth }} × {{ canvasHeight }}px</span>
            </div>
          </div>
        </div>

        <div class="right-panel">
          <div class="settings-card">
            
            <div class="settings-tabs">
              <button class="tab-btn" :class="{ active: activeTab === 'adjust' }" @click="activeTab = 'adjust'">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 5px;">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg> {{ currentLanguage === 'ar' ? 'الصورة' : 'Adjust' }}
              </button>
              <button class="tab-btn" :class="{ active: activeTab === 'elements' }" @click="activeTab = 'elements'">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle; margin-right: 5px;">
                <path d="M12 20h9"></path>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
              </svg> {{ currentLanguage === 'ar' ? 'العناصر' : 'Elements' }}
              </button>
            </div>

            <div class="tab-content" :class="{ 'disabled-overlay': !isImageLoaded }">
              
              <div v-if="activeTab === 'adjust'" class="settings-group">
                <div class="card-title">{{ currentLanguage === 'ar' ? 'الأبعاد والضبط' : 'Size & Filters' }}</div>
                
                <div style="display: flex; gap: 10px; margin-bottom: 15px;">
                  <div style="flex: 1;">
                    <label class="field-label">{{ currentLanguage === 'ar' ? 'العرض' : 'Width' }} (px)</label>
                    <input class="number-input" type="number" v-model.number="canvasWidth" @change="resizeCanvas">
                  </div>
                  <div style="flex: 1;">
                    <label class="field-label">{{ currentLanguage === 'ar' ? 'الارتفاع' : 'Height' }} (px)</label>
                    <input class="number-input" type="number" v-model.number="canvasHeight" @change="resizeCanvas">
                  </div>
                </div>

                <div class="input-group">
                  <label class="field-label">{{ currentLanguage === 'ar' ? 'السطوع' : 'Brightness' }} ({{ filters.brightness }}%)</label>
                  <input type="range" class="range-slider" min="0" max="200" v-model.number="filters.brightness" @input="render" @change="saveState">
                </div>
                
                <div class="input-group">
                  <label class="field-label">{{ currentLanguage === 'ar' ? 'التباين' : 'Contrast' }} ({{ filters.contrast }}%)</label>
                  <input type="range" class="range-slider" min="0" max="200" v-model.number="filters.contrast" @input="render" @change="saveState">
                </div>

                <div class="input-group">
                  <label class="field-label">{{ currentLanguage === 'ar' ? 'التشبع' : 'Saturation' }} ({{ filters.saturation }}%)</label>
                  <input type="range" class="range-slider" min="0" max="200" v-model.number="filters.saturation" @input="render" @change="saveState">
                </div>

                <div class="divider"></div>
                
                <label class="field-label">{{ currentLanguage === 'ar' ? 'الرسم الحر' : 'Free Draw' }}</label>
                <div style="display: flex; gap: 10px; align-items: center; margin-bottom: 15px;">
                  <input type="color" class="color-picker" style="width: 50px;" v-model="drawColor">
                  <input type="range" class="range-slider" min="1" max="50" v-model.number="drawSize">
                  <button class="action-btn-sm" :class="{ 'active-draw': drawMode }" @click="toggleDrawMode">
                    {{ drawMode ? (currentLanguage === 'ar' ? 'إيقاف الرسم' : 'Stop Draw') : (currentLanguage === 'ar' ? 'بدء الرسم' : 'Draw') }}
                  </button>
                </div>
              </div>

              <div v-if="activeTab === 'elements'" class="settings-group">
                <div class="card-title">{{ currentLanguage === 'ar' ? 'إضافة نصوص وأشكال' : 'Add Text & Shapes' }}</div>
                
                <div class="input-group">
                  <input class="number-input" type="text" v-model="textInput" :placeholder="currentLanguage === 'ar' ? 'اكتب نصاً هنا...' : 'Type text here...'">
                </div>
                <div style="display: flex; gap: 10px; margin-bottom: 15px;">
                   <input type="color" class="color-picker" style="flex: 1;" v-model="textColor" title="Text Color">
                   <button class="action-btn" style="flex: 3;" @click="addText">{{ currentLanguage === 'ar' ? 'إضافة نص' : 'Add Text' }}</button>
                </div>

                <div class="divider"></div>

                <div style="display: flex; gap: 10px; margin-bottom: 15px;">
                  <div class="select-wrap" style="flex: 2;">
                    <select v-model="shapeType">
                      <option value="rect">{{ currentLanguage === 'ar' ? 'مستطيل' : 'Rectangle' }}</option>
                      <option value="circle">{{ currentLanguage === 'ar' ? 'دائرة' : 'Circle' }}</option>
                      <option value="line">{{ currentLanguage === 'ar' ? 'خط' : 'Line' }}</option>
                    </select>
                  </div>
                  <input type="color" class="color-picker" style="flex: 1;" v-model="shapeColor" title="Shape Color">
                  <button class="action-btn" style="flex: 2;" @click="addShape">{{ currentLanguage === 'ar' ? 'إضافة' : 'Add' }}</button>
                </div>

                <div v-if="selectedObj" class="selected-panel mt-4">
                  <div class="field-label" style="display:flex; justify-content:space-between;">
                    <span>{{ currentLanguage === 'ar' ? 'تعديل العنصر' : 'Edit Element' }}</span>
                    <button class="icon-btn-danger" @click="deleteSelected">✕</button>
                  </div>
                  
                  <div style="display: flex; gap: 10px; margin-bottom: 10px;">
                    <div style="flex: 1;">
                      <label style="font-size: 0.7rem; color: #716c80;">{{ currentLanguage === 'ar' ? 'الحجم' : 'Scale' }}</label>
                      <input type="range" class="range-slider" min="0.5" max="3" step="0.1" v-model.number="selectedObj.scale" @input="render">
                    </div>
                    <div style="flex: 1;">
                      <label style="font-size: 0.7rem; color: #716c80;">{{ currentLanguage === 'ar' ? 'الشفافية' : 'Opacity' }}</label>
                      <input type="range" class="range-slider" min="0.1" max="1" step="0.1" v-model.number="selectedObj.opacity" @input="render">
                    </div>
                  </div>
                  <div v-if="selectedObj.type === 'text'">
                    <input class="number-input" type="text" v-model="selectedObj.text" @input="render">
                  </div>
                  <div style="margin-top: 10px;">
                    <input type="color" class="color-picker" v-model="selectedObj.color" @input="render">
                  </div>
                </div>

              </div>

            </div>

            <div class="divider"></div>
            <div class="input-group">
               <label class="field-label">{{ currentLanguage === 'ar' ? 'اسم الملف والصيغة' : 'Filename & Format' }}</label>
               <div style="display: flex; gap: 10px;">
                 <input class="number-input" style="flex: 2;" type="text" v-model="fileName">
                 <div class="select-wrap" style="flex: 1;">
                    <select v-model="exportFormat">
                      <option value="image/png">PNG</option>
                      <option value="image/jpeg">JPG</option>
                      <option value="image/webp">WebP</option>
                    </select>
                 </div>
               </div>
            </div>

            <div class="action-area mt-4">
               <button class="download-btn w-100" @click="downloadImage" :disabled="!isImageLoaded">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                 {{ currentLanguage === 'ar' ? 'تحميل التصميم' : 'Download Image' }}
               </button>
            </div>
          </div>
        </div>

      </div>
    </div>
    <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileUpload" />
  </div>
</template>

<script>
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'ImageEditorCom',
  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      isDragging: false,
      isImageLoaded: false,
      activeTab: 'adjust',
      
      canvasCtx: null,
      baseImage: null,
      canvasWidth: 800,
      canvasHeight: 600,
      
      objects: [],
      selectedObj: null,
      dragOffset: { x: 0, y: 0 },
      isDraggingObj: false,
      
      drawMode: false,
      isDrawing: false,
      drawColor: '#3a7afe',
      drawSize: 8,
      
      filters: { brightness: 100, contrast: 100, saturation: 100 },
      
      textInput: '',
      textColor: '#ffffff',
      shapeType: 'rect',
      shapeColor: '#3a7afe',
      
      fileName: 'splendapp-edited',
      exportFormat: 'image/png',
      
      history: [],
      historyIndex: -1
    };
    
  },
  watch: {
    '$store.state.currentLanguage': function(newLang) {
      this.currentLanguage = newLang;
      this.$nextTick(() => {
        if(this.isImageLoaded) {
           this.initCanvas();
           this.render();
        }
      });
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleKeyDown(e) {
      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === 'z') {
          e.preventDefault(); 
          if (e.shiftKey) {
            this.redo();
          } else {
            this.undo();
          }
        } else if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          this.redo();
        }
      }
    },
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.processFile(file);
    },
    handleFileUpload(e) {
      const file = e.target.files[0];
      if (file) this.processFile(file);
      this.$refs.fileInput.value = '';
    },
    createBlankCanvas() {
      this.baseImage = null;
      this.canvasWidth = 800;
      this.canvasHeight = 800;
      this.setupEditor();
    },
    processFile(file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          this.baseImage = img;
          
          const max = 1200;
          let w = img.width, h = img.height;
          if (Math.max(w, h) > max) {
            const r = max / Math.max(w, h);
            w = Math.round(w * r);
            h = Math.round(h * r);
          }
          this.canvasWidth = w;
          this.canvasHeight = h;
          this.fileName = file.name.split('.').slice(0, -1).join('-') + '-edited';
          
          this.setupEditor();
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    setupEditor() {
      this.isImageLoaded = true;
      this.objects = [];
      this.saveState();
      this.selectedObj = null;
      this.filters = { brightness: 100, contrast: 100, saturation: 100 };
      this.history = [];
      this.historyIndex = -1;
      
      this.$nextTick(() => {
        this.initCanvas();
        this.saveState();
        this.render();
      });

    },
    initCanvas() {
      const canvas = this.$refs.editorCanvas;
      if(!canvas) return;
      canvas.width = this.canvasWidth;
      canvas.height = this.canvasHeight;
      this.canvasCtx = canvas.getContext('2d');
    },
    resizeCanvas() {
      if(!this.isImageLoaded) return;
      const canvas = this.$refs.editorCanvas;
      canvas.width = this.canvasWidth;
      canvas.height = this.canvasHeight;
      this.saveState();
      this.render();
    },
    saveState() {
      this.history = this.history.slice(0, this.historyIndex + 1);
      const state = JSON.stringify({
        cw: this.canvasWidth, ch: this.canvasHeight,
        filters: { ...this.filters }, objects: JSON.parse(JSON.stringify(this.objects))
      });
      this.history.push(state);
      if(this.history.length > 25) this.history.shift();
      this.historyIndex = this.history.length - 1;
    },
    undo() {
      if(this.historyIndex > 0) {
        this.historyIndex--;
        this.loadState(this.history[this.historyIndex]);
      }
    },
    redo() {
      if(this.historyIndex < this.history.length - 1) {
        this.historyIndex++;
        this.loadState(this.history[this.historyIndex]);
      }
    },
    loadState(stateStr) {
      const state = JSON.parse(stateStr);
      this.canvasWidth = state.cw; this.canvasHeight = state.ch;
      this.filters = state.filters; this.objects = state.objects;
      this.selectedObj = null; 
      this.$nextTick(() => { this.initCanvas(); this.render(); });
    },
    
    // --- Rendering ---
    getFilterString() {
      return `brightness(${this.filters.brightness}%) contrast(${this.filters.contrast}%) saturate(${this.filters.saturation}%)`;
    },
    render() {
      if(!this.canvasCtx) return;
      
      // Background
      this.canvasCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.canvasCtx.fillStyle = '#ffffff';
      this.canvasCtx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
      
      // Base Image
      if (this.baseImage) {
        this.canvasCtx.save();
        this.canvasCtx.filter = this.getFilterString();
        const scale = Math.min(this.canvasWidth / this.baseImage.width, this.canvasHeight / this.baseImage.height);
        const dw = this.baseImage.width * scale;
        const dh = this.baseImage.height * scale;
        const dx = (this.canvasWidth - dw) / 2;
        const dy = (this.canvasHeight - dh) / 2;
        this.canvasCtx.drawImage(this.baseImage, dx, dy, dw, dh);
        this.canvasCtx.restore();
      }
      
      // Objects
      this.objects.forEach(o => this.drawObject(o));
      
      // Selection Box
      if (this.selectedObj && !this.drawMode) {
        this.drawSelection(this.selectedObj);
      }
    },
    drawObject(o) {
      this.canvasCtx.save();
      this.canvasCtx.globalAlpha = o.opacity || 1;
      this.canvasCtx.translate(o.x, o.y);
      this.canvasCtx.scale(o.scale || 1, o.scale || 1);
      
      if (o.type === 'text') {
        this.canvasCtx.font = `bold 40px 'Cairo', sans-serif`;
        this.canvasCtx.textAlign = 'center';
        this.canvasCtx.textBaseline = 'middle';
        this.canvasCtx.fillStyle = o.color;
        this.canvasCtx.fillText(o.text, 0, 0);
      } 
      else if (o.type === 'rect') {
        this.canvasCtx.fillStyle = o.color;
        this.canvasCtx.fillRect(-o.w/2, -o.h/2, o.w, o.h);
      }
      else if (o.type === 'circle') {
        this.canvasCtx.fillStyle = o.color;
        this.canvasCtx.beginPath();
        this.canvasCtx.arc(0, 0, o.r, 0, Math.PI * 2);
        this.canvasCtx.fill();
      }
      else if (o.type === 'line') {
        this.canvasCtx.strokeStyle = o.color;
        this.canvasCtx.lineWidth = 10;
        this.canvasCtx.beginPath();
        this.canvasCtx.moveTo(-o.w/2, 0);
        this.canvasCtx.lineTo(o.w/2, 0);
        this.canvasCtx.stroke();
      }
      else if (o.type === 'draw') {
        this.canvasCtx.strokeStyle = o.color;
        this.canvasCtx.lineWidth = o.lineWidth || 8;
        this.canvasCtx.lineCap = 'round';
        this.canvasCtx.lineJoin = 'round';
        this.canvasCtx.beginPath();
        (o.points || []).forEach((pt, i) => {
          if (i === 0) this.canvasCtx.moveTo(pt.x, pt.y);
          else this.canvasCtx.lineTo(pt.x, pt.y);
        });
        this.canvasCtx.stroke();
      }
      
      this.canvasCtx.restore();
    },
    drawSelection(o) {
      const b = this.getBounds(o);
      this.canvasCtx.save();
      this.canvasCtx.strokeStyle = '#3a7afe';
      this.canvasCtx.lineWidth = 2;
      this.canvasCtx.setLineDash([6, 6]);
      this.canvasCtx.strokeRect(o.x - b.w/2 - 5, o.y - b.h/2 - 5, b.w + 10, b.h + 10);
      this.canvasCtx.fillStyle = '#ef4444';
      this.canvasCtx.beginPath();
      this.canvasCtx.arc(o.x + b.w/2 + 5, o.y - b.h/2 - 5, 10, 0, Math.PI * 2);
      this.canvasCtx.fill();
      this.canvasCtx.strokeStyle = '#ffffff';
      this.canvasCtx.lineWidth = 2;
      this.canvasCtx.beginPath();
      this.canvasCtx.moveTo(o.x + b.w/2 + 2, o.y - b.h/2 - 8);
      this.canvasCtx.lineTo(o.x + b.w/2 + 8, o.y - b.h/2 - 2);
      this.canvasCtx.moveTo(o.x + b.w/2 + 8, o.y - b.h/2 - 8);
      this.canvasCtx.lineTo(o.x + b.w/2 + 2, o.y - b.h/2 - 2);
      this.canvasCtx.stroke();
      this.canvasCtx.restore();
    },
    getBounds(o) {
      let w = 100, h = 100;
      if (o.type === 'text') {
        this.canvasCtx.font = `bold 40px 'Cairo', sans-serif`;
        w = this.canvasCtx.measureText(o.text).width;
        h = 40;
      } else if (o.type === 'rect') { w = o.w; h = o.h; }
      else if (o.type === 'circle') { w = o.r*2; h = o.r*2; }
      else if (o.type === 'line') { w = o.w; h = 20; }
      else if (o.type === 'draw') {
        if(!o.points.length) return {w:50, h:50};
        const xs = o.points.map(p=>p.x), ys = o.points.map(p=>p.y);
        w = Math.max(Math.max(...xs) - Math.min(...xs), 20);
        h = Math.max(Math.max(...ys) - Math.min(...ys), 20);
      }
      return { w: w * (o.scale||1), h: h * (o.scale||1) };
    },

    // --- Interaction ---
    getPointerPos(e) {
      const canvas = this.$refs.editorCanvas;
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return {
        x: (clientX - rect.left) * (canvas.width / rect.width),
        y: (clientY - rect.top) * (canvas.height / rect.height)
      };
    },
    hitTest(p) {
      for (let i = this.objects.length - 1; i >= 0; i--) {
        let o = this.objects[i];
        let b = this.getBounds(o);
        if (p.x >= o.x - b.w/2 && p.x <= o.x + b.w/2 && p.y >= o.y - b.h/2 && p.y <= o.y + b.h/2) {
          return o;
        }
      }
      return null;
    },
    startAction(e) {
      const p = this.getPointerPos(e);
      if (this.selectedObj && !this.drawMode) {
        const b = this.getBounds(this.selectedObj);
        const btnX = this.selectedObj.x + b.w/2 + 5;
        const btnY = this.selectedObj.y - b.h/2 - 5;
        const dist = Math.hypot(p.x - btnX, p.y - btnY);
        
        if (dist <= 15) { 
          this.deleteSelected();
          return; 
        }
      }
      if (this.drawMode) {
        this.isDrawing = true;
        this.selectedObj = {
          id: Date.now(), type: 'draw', x: p.x, y: p.y, points: [{x:0, y:0}],
          color: this.drawColor, lineWidth: this.drawSize, scale: 1, opacity: 1
        };
        this.objects.push(this.selectedObj);
      } else {
        const hit = this.hitTest(p);
        if (hit) {
          this.selectedObj = hit;
          this.isDraggingObj = true;
          this.dragOffset = { x: p.x - hit.x, y: p.y - hit.y };
          this.activeTab = 'elements';
        } else {
          this.selectedObj = null;
        }
      }
      this.render();
    },
    moveAction(e) {
      if (this.drawMode && this.isDrawing && this.selectedObj) {
        const p = this.getPointerPos(e);
        this.selectedObj.points.push({ x: p.x - this.selectedObj.x, y: p.y - this.selectedObj.y });
        this.render();
      } else if (this.isDraggingObj && this.selectedObj) {
        const p = this.getPointerPos(e);
        this.selectedObj.x = p.x - this.dragOffset.x;
        this.selectedObj.y = p.y - this.dragOffset.y;
        this.render();
      }
    },
    endAction() {
      if (this.drawMode && this.isDrawing) {
        this.isDrawing = false;
        // Normalize coordinates relative to bounds center
        if(this.selectedObj && this.selectedObj.points.length > 1) {
            const xs = this.selectedObj.points.map(p=>p.x);
            const ys = this.selectedObj.points.map(p=>p.y);
            const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
            const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
            this.selectedObj.points = this.selectedObj.points.map(p => ({ x: p.x - cx, y: p.y - cy }));
            this.selectedObj.x += cx;
            this.selectedObj.y += cy;
        }
        this.saveState();
        this.toggleDrawMode(); // Auto turn off drawing
      } else if (this.isDraggingObj) {
        this.isDraggingObj = false;
        this.saveState();
      }
    },

    // --- Object Management ---
    addText() {
      this.objects.push({
        id: Date.now(), type: 'text', text: this.textInput || (this.currentLanguage === 'ar' ? 'نص جديد' : 'New Text'),
        x: this.canvasWidth/2, y: this.canvasHeight/2, color: this.textColor, scale: 1, opacity: 1
      });
      this.selectedObj = this.objects[this.objects.length - 1];
      this.saveState();
      this.render();
    },
    addShape() {
      let o = { id: Date.now(), type: this.shapeType, x: this.canvasWidth/2, y: this.canvasHeight/2, color: this.shapeColor, scale: 1, opacity: 1 };
      if (this.shapeType === 'rect') { o.w = 200; o.h = 100; }
      else if (this.shapeType === 'circle') { o.r = 60; }
      else if (this.shapeType === 'line') { o.w = 200; }
      this.objects.push(o);
      this.selectedObj = o;
      this.saveState();
      this.render();
    },
    deleteSelected() {
      if(!this.selectedObj) return;
      this.objects = this.objects.filter(o => o.id !== this.selectedObj.id);
      this.selectedObj = null;
      this.saveState();
      this.saveState();
      this.render();
    },
    toggleDrawMode() {
      this.drawMode = !this.drawMode;
      this.selectedObj = null;
      this.render();
    },

    // --- History ---
    saveState() {
      const state = JSON.stringify({
        cw: this.canvasWidth, ch: this.canvasHeight,
        filters: { ...this.filters }, objects: JSON.parse(JSON.stringify(this.objects))
      });
      
      // Remove future states if we overwrite from middle of history
      this.history = this.history.slice(0, this.historyIndex + 1);
      this.history.push(state);
      if(this.history.length > 20) this.history.shift();
      this.historyIndex = this.history.length - 1;
    },
    undo() {
      if(this.historyIndex > 0) {
        this.historyIndex--;
        this.loadState(this.history[this.historyIndex]);
      }
    },
    redo() {
      if(this.historyIndex < this.history.length - 1) {
        this.historyIndex++;
        this.loadState(this.history[this.historyIndex]);
      }
    },
    loadState(stateStr) {
      const state = JSON.parse(stateStr);
      this.canvasWidth = state.cw;
      this.canvasHeight = state.ch;
      this.filters.brightness = state.filters.brightness;
      this.filters.contrast = state.filters.contrast;
      this.filters.saturation = state.filters.saturation;
      this.filters = state.filters;
      this.objects = state.objects;
      this.selectedObj = null; // Clear selection on undo/redo to avoid mismatches
      this.$nextTick(() => {
        this.initCanvas();
        this.render();
      });
    },

    // --- Export ---
    downloadImage() {
      const canvas = this.$refs.editorCanvas;
      if (!canvas) return;
      
      // Temporarily clear selection box for export
      const tempSelected = this.selectedObj;
      this.selectedObj = null;
      this.render();
      
      const link = document.createElement('a');
      const ext = this.exportFormat === 'image/jpeg' ? 'jpg' : this.exportFormat === 'image/webp' ? 'webp' : 'png';
      link.download = `${this.fileName}.${ext}`;
      link.href = canvas.toDataURL(this.exportFormat, 0.92);
      link.click();
      notifyToolSuccess('image-editor');
      
      // Restore selection box
      this.selectedObj = tempSelected;
      this.render();
    },
    reset() {
      this.isImageLoaded = false;
      this.baseImage = null;
      this.objects = [];
      this.selectedObj = null;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', 'Cairo', sans-serif;
  max-width: 1200px; 
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: 100vh;
}
[dir="rtl"] .compressor-wrapper { direction: rtl; text-align: right; }

.main-layout { display: flex; gap: 25px; align-items: flex-start; justify-content: center; }
.main-layout:not(.has-image) .left-panel { max-width: 600px; margin: 0 auto; }

.left-panel { flex: 1; min-width: 0; width: 100%; transition: 0.3s; }
.right-panel { width: 340px; flex-shrink: 0; position: sticky; top: 25px; }

@media (max-width: 950px) {
  .main-layout { flex-direction: column; }
  .right-panel { width: 100%; position: static; }
}

/* ── Drop Zone ── */
.drop-zone {
  background: white; border: 2.5px dashed #c7c9f5; border-radius: 20px;
  padding: 4rem 2rem; text-align: center; cursor: pointer;
  transition: border-color .2s, background .2s;
}
.drop-zone.dragging { border-color: #3a7afe; background: #eef0ff; }
.drop-icon svg { width: 52px; height: 52px; fill: #3a7afe; margin-bottom: 1rem; opacity: .85; }
.drop-text { font-size: 1.1rem; font-weight: 600; color: #24365c; margin: 0 0 .35rem; }
.drop-hint { color: #716c80; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #716c80; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe; color: white; border: none; border-radius: 10px;
  padding: .6rem 1.6rem; font-family: inherit; font-size: .95rem;
  font-weight: 600; cursor: pointer; transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #0053fe; transform: translateY(-1px); }

/* ── Canvas Workspace ── */
.preview-card {
  background: white; border-radius: 16px; overflow: hidden;
  border: 1.5px solid #e5e7eb; display: flex; flex-direction: column;
  box-shadow: 0 4px 15px rgba(0,0,0,0.02);
}
.preview-label {
  font-size: .85rem; font-weight: 700; color: #24365c; padding: .8rem 1rem; 
  border-bottom: 1px solid #e5e7eb; background: #f9fafb; 
}
.canvas-container {
  padding: 20px; background: repeating-conic-gradient(#f3f4f6 0% 25%, transparent 0% 50%) 50% / 20px 20px;
  display: flex; justify-content: center; align-items: center; min-height: 400px; overflow: auto;
}
.editor-canvas {
  max-width: 100%; max-height: 65vh; object-fit: contain; box-shadow: 0 4px 15px rgba(0,0,0,0.1); border-radius: 4px;
  cursor: grab;
}
.editor-canvas:active { cursor: grabbing; }
.canvas-container.draw-mode .editor-canvas { cursor: crosshair; }

.canvas-footer {
  padding: 8px 15px; font-size: 0.75rem; color: #716c80; font-weight: bold; font-family: 'DM Mono', monospace;
  border-top: 1px solid #e5e7eb; background: #f9fafb; text-align: center;
}

/* ── Settings Panel ── */
.settings-card { 
  background: white; border-radius: 16px; padding: 20px; border: 1.5px solid #e5e7eb; box-shadow: 0 4px 15px rgba(0,0,0,0.02);
}
.card-title { font-weight: 700; font-size: 1.05rem; color: #24365c; margin-bottom: 1rem; }

.disabled-overlay { opacity: 0.5; pointer-events: none; }

.settings-tabs {
  display: flex; background: #f3f4f6; border-radius: 12px; padding: 4px; margin-bottom: 20px;
}
.tab-btn {
  flex: 1; padding: 10px; border: none; background: transparent; border-radius: 8px; font-family: inherit;
  font-size: 0.9rem; font-weight: 700; color: #716c80; cursor: pointer; transition: 0.2s;
}
.tab-btn.active { background: white; color: #3a7afe; box-shadow: 0 2px 6px rgba(0,0,0,0.05); }

.divider { height: 1px; background: #e5e7eb; margin: 20px 0; }
.input-group { margin-bottom: 15px; }
.field-label { display: block; font-size: .8rem; font-weight: 600; color: #716c80; margin-bottom: .6rem; }

.number-input {
  width: 100%; border: 1.5px solid #e5e7eb; border-radius: 10px; padding: .6rem .9rem; font-family: inherit; font-size: .95rem;
  color: #24365c; background: #f9fafb; outline: none; transition: border-color .2s;
}
.number-input:focus { border-color: #3a7afe; }

.color-picker {
  height: 40px; border: 1.5px solid #e5e7eb; border-radius: 8px; padding: 1px; cursor: pointer; background: white; width: 100%;
}

.select-wrap { position: relative; }
.select-wrap select {
  width: 100%; appearance: none; background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 10px; padding: .6rem .9rem; 
  font-family: inherit; font-size: .9rem; color: #24365c; cursor: pointer; outline: none; transition: border-color .2s;
}
.select-wrap select:focus { border-color: #3a7afe; }

.range-slider {
  -webkit-appearance: none; width: 100%; height: 6px; background: #e5e7eb; border-radius: 5px; outline: none;
}
.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #3a7afe; cursor: pointer; transition: 0.2s;
}
.range-slider::-webkit-slider-thumb:hover { transform: scale(1.2); }

/* ── Buttons ── */
.action-btn { background: #eef0ff; color: #3a7afe; border: none; border-radius: 10px; padding: .6rem; font-weight: 700; cursor: pointer; width: 100%; transition: 0.2s; }
.action-btn:hover { background: #3a7afe; color: white; }

.action-btn-sm { background: #eef0ff; color: #3a7afe; border: none; border-radius: 8px; padding: .4rem .8rem; font-weight: 700; cursor: pointer; font-size: 0.8rem; transition: 0.2s; }
.action-btn-sm:hover { background: #3a7afe; color: white; }
.action-btn-sm.active-draw { background: #ef4444; color: white; }

.icon-btn { background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 8px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #24365c; font-weight: bold; }
.icon-btn:hover:not(:disabled) { border-color: #3a7afe; color: #3a7afe; }
.icon-btn:disabled { color: #d1d5db; cursor: not-allowed; }

.icon-btn-danger { background: #fef2f2; border: 1px solid #fecaca; color: #ef4444; border-radius: 6px; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 0.7rem; }
.icon-btn-danger:hover { background: #ef4444; color: white; }

.selected-panel { background: #f9fafb; border: 1.5px solid #e5e7eb; border-radius: 12px; padding: 12px; }

.download-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem; background: #3a7afe; color: white; 
  border: none; border-radius: 12px; padding: .85rem; font-family: inherit; font-size: 1rem; font-weight: 700; cursor: pointer;
  transition: background .2s, transform .1s; box-shadow: 0 4px 16px rgba(58, 122, 254, .3);
}
.download-btn.w-100 { width: 100%; }
.download-btn svg { width: 18px; height: 18px; }
.download-btn:hover:not(:disabled) { background: #0053fe; transform: translateY(-2px); }
.download-btn:disabled { background: #9ca3af; cursor: not-allowed; box-shadow: none; transform: none; }

.reset-icon-btn { background: none; border: none; color: #ef4444; font-size: 0.8rem; font-weight: bold; cursor: pointer; padding: 0; }
.reset-icon-btn:hover { text-decoration: underline; color: #dc2626; }
</style>
